import { tr } from '@/lib/i18n-core';
import { useState, type FormEvent, type ReactNode } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUPPLIER_PAYMENT_STATUS, toMajorString } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import {
  cn,
  dateOnly,
  dateTime,
  humanize,
  money,
  toIsoDate,
  todayIso,
  type CsvColumn,
} from '@/lib/utils';
import { DataTable, Dialog, EmptyState, FilterBar, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Select, StatusBadge, TableSkeleton, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { CsvExportButton } from './CsvExportButton';
import { PrintDocumentButton } from './PrintDocumentButton';
import { ReasonDialog } from './ReasonDialog';
import { parseMoneyInput } from '@/components/MoneyInput';
import {
  canReversePayment,
  hasVoucher,
  signedStatementAmount,
  statementCsvColumns,
  statementKindLabel,
  statementKindVariant,
  type StatementRow,
} from './payables-view';

const PAGE_SIZE = 25;
export const OPEN_GRN_PAGE_SIZE = 50;
export const BATCH_SEARCH_LIMIT = 50;
const STATEMENT_DEFAULT_DAYS = 90;
const STATEMENT_MAX_DAYS = 366;

const READ_PERMISSIONS = [
  'supplier.payment.create',
  'supplier.payment.approve',
  'purchase.manage',
  'purchase.return',
  'gl.read',
  'report.financial',
];

const TAB_IDS = ['ageing', 'statement', 'payments', 'returns'] as const;
type TabId = (typeof TAB_IDS)[number];

export interface SupplierOption {
  id: string;
  code: string;
  name: string;
  paymentTermsDays: number;
  isActive: boolean;
}

export interface StoreOption {
  id: string;
  name: string;
  branch: { id: string; name: string } | null;
}

interface LedgerTie {
  subledgerMinor: number;
  glSubledgerSourcesMinor: number;
  glOtherMinor: number;
  glTotalMinor: number;
  differenceMinor: number;
  reconciled: boolean;
}

interface AgeingSupplier {
  supplierId: string;
  name: string;
  code: string;
  paymentTermsDays: number;
  buckets: number[];
  unappliedMinor: number;
  billedMinor: number;
  returnedMinor: number;
  paidMinor: number;
  balanceMinor: number;
  overdueMinor: number;
  notYetDueMinor: number;
  oldestDueDate: string | null;
}

interface AgeingReport {
  asOf: string;
  bucketLabels: string[];
  supplierCount: number;
  suppliers: AgeingSupplier[];
  totals: { buckets: number[]; unappliedMinor: number; balanceMinor: number; overdueMinor: number };
  ledgerTie: LedgerTie | null;
}

interface SupplierStatement {
  supplier: { id: string; code: string; name: string; phone: string | null; taxNumber: string | null; paymentTermsDays: number };
  from: string;
  to: string;
  openingBalanceMinor: number;
  chargesMinor: number;
  creditsMinor: number;
  closingBalanceMinor: number;
  pendingApprovalMinor: number;
  totalRows: number;
  rows: StatementRow[];
}

interface SupplierPayment {
  id: string;
  number: string;
  supplierId: string;
  supplier: { id: string; name: string };
  mode: string;
  status: string;
  amountMinor: number;
  reference: string | null;
  notes: string | null;
  paidAt: string | null;
  requestedAt: string;
  requestedByName: string | null;
  approvedByName: string | null;
  decisionNote: string | null;
  allocationCount: number;
  reversedAt?: string | null;
  reversalReason?: string | null;
  reversedByName?: string | null;
}

export interface OpenGrn {
  grnId: string;
  number: string;
  supplierInvoiceNo: string | null;
  receivedAt: string;
  totalMinor: number;
  paidMinor: number;
  returnedMinor: number;
  outstandingMinor: number;
  dueDate: string | null;
  daysOverdue: number;
}

export interface PaymentResult {
  id: string;
  number: string;
  status: 'paid' | 'pending_approval';
  amountMinor: number;
  allocatedMinor: number;
  unallocatedMinor: number;
  supplierName: string;
}

export interface AllocationDraft {
  text: string;
  outstandingMinor: number;
}

interface PurchaseReturnSummary {
  id: string;
  number: string;
  supplier: { id: string; name: string };
  store: { id: string; name: string };
  grn: { id: string; number: string } | null;
  reason: string;
  creditNoteNo: string | null;
  lines: number;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  stockCostMinor?: number;
  returnedAt: string;
}

interface PurchaseReturnItem {
  id: string;
  batchNo: string;
  expiryDate: string;
  qty: number;
  unitCostMinor: number;
  costMinor: number;
  stockCostMinor?: number;
  taxMinor: number;
  totalMinor: number;
  product: { code: string; genericName: string; unit: string };
}

interface PurchaseReturnDetail {
  id: string;
  number: string;
  supplier: { id: string; name: string };
  store: { id: string; name: string };
  grn: { id: string; number: string; supplierInvoiceNo: string | null } | null;
  reason: string;
  creditNoteNo: string | null;
  notes: string | null;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  stockCostMinor?: number;
  returnedAt: string;
  items: PurchaseReturnItem[];
}

export interface StockBatch {
  id: string;
  product: { code: string; genericName: string; brandName: string | null; strength: string | null; unit: string };
  store: { id: string; name: string };
  batchNo: string;
  expiryDate: string;
  qtyOnHand: number;
  purchasePriceMinor: number;
  salePriceMinor: number;
  status?: string;
  statusReason?: string | null;
}

export interface ReturnLineDraft {
  batch: StockBatch;
  qty: string;
}

export interface PurchaseReturnResult {
  id: string;
  number: string;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  stockCostMinor?: number;
  priceVarianceMinor?: number;
  lines: number;
}

export function storeLabel(store: StoreOption): string {
  return store.branch ? `${store.branch.name} — ${store.name}` : store.name;
}

export function supplierLabel(supplier: SupplierOption): string {
  return supplier.isActive ? supplier.name : `${supplier.name} (inactive)`;
}

export function batchLabel(batch: StockBatch): string {
  const { product } = batch;
  return `${product.brandName ?? product.genericName} ${product.strength ?? ''}`.trim();
}

export function parseMinor(text: string): number {
  return parseMoneyInput(text) ?? 0;
}

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
}

function daysAgoIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toIsoDate(date);
}

function statementRangeProblem(from: string, to: string): string | null {
  if (!isIsoDate(from) || !isIsoDate(to)) return 'Choose both dates.';
  if (from > to) return 'The start date is after the end date.';
  const days = (Date.parse(to) - Date.parse(from)) / 86_400_000 + 1;
  if (days > STATEMENT_MAX_DAYS) return 'A statement covers at most a year at a time.';
  return null;
}

function tabFromParams(params: URLSearchParams): TabId {
  const requested = params.get('tab');
  const match = TAB_IDS.find((id) => id === requested);
  if (match) return match;
  return params.get('supplier') ? 'statement' : 'ageing';
}

export function unallocatedWhenOldestFirst(amountMinor: number, loadedOutstandingMinor: number, hasMoreGrns: boolean): number | null {
  if (amountMinor <= loadedOutstandingMinor) return 0;
  return hasMoreGrns ? null : amountMinor - loadedOutstandingMinor;
}

function ageingCsvColumns(bucketLabels: string[]): CsvColumn<AgeingSupplier>[] {
  return [
    { header: tr('pharmacy', 'code'), value: (row) => row.code },
    { header: tr('pharmacy', 'supplier'), value: (row) => row.name },
    { header: tr('pharmacy', 'terms_days'), value: (row) => row.paymentTermsDays },
    ...bucketLabels.map((label, index) => ({
      header: label,
      value: (row: AgeingSupplier) => toMajorString(row.buckets[index] ?? 0),
    })),
    { header: tr('pharmacy', 'on_account'), value: (row) => toMajorString(row.unappliedMinor) },
    { header: tr('pharmacy', 'not_yet_due'), value: (row) => toMajorString(row.notYetDueMinor) },
    { header: tr('pharmacy', 'overdue'), value: (row) => toMajorString(row.overdueMinor) },
    { header: tr('pharmacy', 'oldest_due'), value: (row) => row.oldestDueDate },
    { header: tr('pharmacy', 'balance'), value: (row) => toMajorString(row.balanceMinor) },
  ];
}

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => get<SupplierOption[]>('/pharmacy/suppliers'),
  });
}

function LoadingBox() {
  return (
    <TableSkeleton />
  );
}

function Figure({ minor }: { minor: number }) {
  if (minor === 0) return <span className="text-muted-foreground">{money(0)}</span>;
  return <>{money(minor)}</>;
}

export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

export function Pager({
  offset,
  pageSize,
  shown,
  total,
  hasNext,
  busy,
  onChange,
}: {
  offset: number;
  pageSize: number;
  shown: number;
  total?: number;
  hasNext: boolean;
  busy?: boolean;
  onChange: (offset: number) => void;
}) {
  if (offset === 0 && !hasNext) return null;
  const range =
    shown === 0
      ? 'Nothing on this page'
      : `${offset + 1}–${offset + shown}${total === undefined ? '' : ` of ${total}`}`;
  return (
    <div className="flex w-full items-center justify-between gap-3 text-sm text-muted-foreground">
      <span>{range}</span>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || offset === 0}
          onClick={() => onChange(Math.max(0, offset - pageSize))}
        >
          {tr('pharmacy', 'previous')}
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={busy || !hasNext} onClick={() => onChange(offset + pageSize)}>
          {tr('pharmacy', 'next')}
        </Button>
      </div>
    </div>
  );
}

export function PayablesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<TabId>(() => tabFromParams(params));
  const [statementSupplierId, setStatementSupplierId] = useState(() => params.get('supplier') ?? '');

  const openStatement = (supplierId: string) => {
    setStatementSupplierId(supplierId);
    setTab('statement');
  };

  return (
    <>
      <PageHeader
        title={tr('pharmacy', 'supplier_payables')}
        description={tr('pharmacy', 'what_is_owed_to_suppliers_what_has_been_paid')}
        actions={
          <>
            {tab === 'payments' && can('supplier.payment.create') && (
              <Button onClick={() => navigate(`/payables/payments/new?returnTo=${encodeURIComponent('/payables?tab=payments')}`)}>
                {tr('pharmacy', 'pay_supplier')}
              </Button>
            )}
            {tab === 'returns' && can('purchase.return', 'purchase.manage') && (
              <Button onClick={() => navigate(`/payables/returns/new?returnTo=${encodeURIComponent('/payables?tab=returns')}`)}>
                {tr('pharmacy', 'return_goods')}
              </Button>
            )}
          </>
        }
      />

      {!can(...READ_PERMISSIONS) ? (
        <Alert>{tr('pharmacy', 'your_role_does_not_include_access_to_supplier')}</Alert>
      ) : (
        <>
          <Tabs
            active={tab}
            onChange={(id) => setTab(TAB_IDS.find((known) => known === id) ?? 'ageing')}
            tabs={[
              { id: 'ageing', label: tr('pharmacy', 'ageing') },
              { id: 'statement', label: tr('pharmacy', 'statement') },
              { id: 'payments', label: tr('pharmacy', 'payments') },
              { id: 'returns', label: tr('pharmacy', 'returns') },
            ]}
          />

          {tab === 'ageing' && <AgeingTab onOpenStatement={openStatement} />}
          {tab === 'statement' && <StatementTab supplierId={statementSupplierId} onSupplierChange={setStatementSupplierId} />}
          {tab === 'payments' && <PaymentsTab />}
          {tab === 'returns' && <ReturnsTab />}
        </>
      )}
    </>
  );
}

function AgeingTab({ onOpenStatement }: { onOpenStatement: (supplierId: string) => void }) {
  const [draftAsOf, setDraftAsOf] = useState(todayIso);
  const [asOf, setAsOf] = useState(todayIso);
  const [offset, setOffset] = useState(0);

  const ageing = useQuery({
    queryKey: ['payables', 'ageing', asOf, offset],
    queryFn: () => get<AgeingReport>(`/pharmacy/payables/ageing${qs({ asOf, limit: PAGE_SIZE, offset })}`),
    placeholderData: keepPreviousData,
  });

  const report = ageing.data;

  const show = (event: FormEvent) => {
    event.preventDefault();
    if (!isIsoDate(draftAsOf)) return;
    if (draftAsOf === asOf && offset === 0) {
      void ageing.refetch();
      return;
    }
    setAsOf(draftAsOf);
    setOffset(0);
  };

  return (
    <>
      <form onSubmit={show}>
        <FilterBar
          trailing={
            <>
              <PrintDocumentButton
                path="/pharmacy/print/ageing"
                params={{ asOf }}
                label={tr('pharmacy', 'print_ageing')}
                size="default"
                disabled={!report || report.suppliers.length === 0}
              />
              <CsvExportButton<AgeingReport, AgeingSupplier>
                path="/pharmacy/payables/ageing"
                params={{ asOf }}
                pickRows={(response) => response.suppliers}
                columns={ageingCsvColumns(report?.bucketLabels ?? [])}
                filename={`supplier-ageing-${asOf}`}
                size="default"
                disabled={!report || report.suppliers.length === 0}
              />
            </>
          }
        >
          <FilterField label={tr('pharmacy', 'balances_as_of')}>
            <DatePicker className="w-44" value={draftAsOf} onChange={setDraftAsOf} />
          </FilterField>
          <Button type="submit" variant="secondary" disabled={!isIsoDate(draftAsOf)} loading={ageing.isFetching}>
            {tr('pharmacy', 'show')}
          </Button>
        </FilterBar>
      </form>

      {ageing.isLoading && <LoadingBox />}
      {ageing.error && (
        <Alert variant="destructive" title={tr('pharmacy', 'the_ageing_report_could_not_be_loaded')}>
          {describeError(ageing.error)}
        </Alert>
      )}

      {report && (
        <>
          {report.ledgerTie && <LedgerTieCard tie={report.ledgerTie} />}
          <AgeingTable report={report} onOpenStatement={onOpenStatement} />
          <Pager
            offset={offset}
            pageSize={PAGE_SIZE}
            shown={report.suppliers.length}
            total={report.supplierCount}
            hasNext={offset + report.suppliers.length < report.supplierCount}
            busy={ageing.isFetching}
            onChange={setOffset}
          />
        </>
      )}
    </>
  );
}

function LedgerTieCard({ tie }: { tie: LedgerTie }) {
  return (
    <Card className={cn('mb-4', !tie.reconciled && 'border-destructive/40')}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>{tr('pharmacy', 'tie_to_the_ledger')}</CardTitle>
        {tie.reconciled ? (
          <Badge variant="success">{tr('pharmacy', 'ties_to_ledger_2100')}</Badge>
        ) : (
          <Badge variant="destructive">{tr('pharmacy', 'differs_by_x', { value: money(Math.abs(tie.differenceMinor)) })}</Badge>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Detail label={tr('pharmacy', 'supplier_balances_in_this_report')}>
            <span className="tabular">{money(tie.subledgerMinor)}</span>
          </Detail>
          <Detail label={tr('pharmacy', 'ledger_2100_from_purchasing_and_payments')}>
            <span className="tabular">{money(tie.glSubledgerSourcesMinor)}</span>
          </Detail>
          <Detail label={tr('pharmacy', 'manual_journals_on_2100')}>
            <span className="tabular">{money(tie.glOtherMinor)}</span>
          </Detail>
          <Detail label={tr('pharmacy', 'ledger_2100_in_total')}>
            <span className="tabular">{money(tie.glTotalMinor)}</span>
          </Detail>
        </dl>
        <p className="mt-3 text-xs text-muted-foreground">
          {tr('pharmacy', 'the_supplier_balances_should_equal_what')}
        </p>
      </CardContent>
    </Card>
  );
}

const HEAD_CELL = 'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground';

function AgeingTable({ report, onOpenStatement }: { report: AgeingReport; onOpenStatement: (supplierId: string) => void }) {
  if (report.suppliers.length === 0) {
    return (
      <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
        {tr('pharmacy', 'nothing_is_owed_to_any_supplier_as_of_x_goods', { asOf: dateOnly(report.asOf) })}
      </div>
    );
  }

  const oldestBucket = report.bucketLabels.length - 1;

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className={cn(HEAD_CELL, 'text-start')}>{tr('pharmacy', 'supplier')}</th>
            {report.bucketLabels.map((label) => (
              <th key={label} className={cn(HEAD_CELL, 'text-end')}>
                {label}
              </th>
            ))}
            <th
              className={cn(HEAD_CELL, 'text-end')}
              title={tr('pharmacy', 'payments_and_credits_not_yet_matched_to_goods')}
            >
              {tr('pharmacy', 'on_account')}
            </th>
            <th
              className={cn(HEAD_CELL, 'text-end')}
              title={tr('pharmacy', 'outstanding_goods_received_that_are_past_their')}
            >
              {tr('pharmacy', 'overdue')}
            </th>
            <th className={cn(HEAD_CELL, 'text-end')}>{tr('pharmacy', 'oldest_due')}</th>
            <th className={cn(HEAD_CELL, 'text-end')}>{tr('pharmacy', 'balance')}</th>
          </tr>
        </thead>
        <tbody>
          {report.suppliers.map((supplier) => (
            <tr
              key={supplier.supplierId}
              tabIndex={0}
              onClick={() => onOpenStatement(supplier.supplierId)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onOpenStatement(supplier.supplierId);
              }}
              className="cursor-pointer border-b hover:bg-accent/50 focus-ring"
            >
              <td className="px-3 py-2.5">
                <p className="font-medium">{supplier.name}</p>
                <p className="text-xs text-muted-foreground">
                  {supplier.code} {tr('pharmacy', 'terms')} {supplier.paymentTermsDays} {tr('pharmacy', 'days')}
                </p>
              </td>
              {supplier.buckets.map((value, index) => (
                <td
                  key={report.bucketLabels[index] ?? index}
                  className={cn('px-3 py-2.5 text-end tabular', index === oldestBucket && value > 0 && 'font-medium text-destructive')}
                >
                  <Figure minor={value} />
                </td>
              ))}
              <td className="px-3 py-2.5 text-end tabular">
                <Figure minor={supplier.unappliedMinor} />
                {supplier.unappliedMinor < 0 && <span className="ms-1 text-xs text-muted-foreground">{tr('pharmacy', 'advance')}</span>}
              </td>
              <td className={cn('px-3 py-2.5 text-end tabular', supplier.overdueMinor > 0 && 'font-medium text-destructive')}>
                <Figure minor={supplier.overdueMinor} />
              </td>
              <td className="px-3 py-2.5 text-end tabular">
                {supplier.oldestDueDate ? dateOnly(supplier.oldestDueDate) : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2.5 text-end font-semibold tabular">{money(supplier.balanceMinor)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-muted/50 font-semibold">
            <td className="px-3 py-2.5">{`All ${report.supplierCount} supplier${report.supplierCount === 1 ? '' : 's'}`}</td>
            {report.totals.buckets.map((value, index) => (
              <td key={report.bucketLabels[index] ?? index} className="px-3 py-2.5 text-end tabular">
                {money(value)}
              </td>
            ))}
            <td className="px-3 py-2.5 text-end tabular">{money(report.totals.unappliedMinor)}</td>
            <td className={cn('px-3 py-2.5 text-end tabular', report.totals.overdueMinor > 0 && 'text-destructive')}>
              {money(report.totals.overdueMinor)}
            </td>
            <td className="px-3 py-2.5" />
            <td className="px-3 py-2.5 text-end tabular">{money(report.totals.balanceMinor)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function StatementTab({
  supplierId,
  onSupplierChange,
}: {
  supplierId: string;
  onSupplierChange: (supplierId: string) => void;
}) {
  const suppliers = useSuppliers();
  const [draft, setDraft] = useState(() => ({ from: daysAgoIso(STATEMENT_DEFAULT_DAYS - 1), to: todayIso() }));
  const [range, setRange] = useState(draft);
  const [offset, setOffset] = useState(0);

  const rangeProblem = statementRangeProblem(draft.from, draft.to);

  const statement = useQuery({
    queryKey: ['payables', 'statement', supplierId, range.from, range.to, offset],
    queryFn: () =>
      get<SupplierStatement>(
        `/pharmacy/payables/suppliers/${supplierId}/statement${qs({ from: range.from, to: range.to, limit: PAGE_SIZE, offset })}`,
      ),
    enabled: Boolean(supplierId),
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === supplierId ? previous : undefined),
  });

  const data = statement.data;

  const show = (event: FormEvent) => {
    event.preventDefault();
    if (rangeProblem || !supplierId) return;
    if (draft.from === range.from && draft.to === range.to && offset === 0) {
      void statement.refetch();
      return;
    }
    setRange(draft);
    setOffset(0);
  };

  return (
    <>
      <form onSubmit={show}>
        <FilterBar
          trailing={
            supplierId ? (
              <>
                <PrintDocumentButton
                  path={`/pharmacy/print/supplier-statement/${supplierId}`}
                  params={{ from: range.from, to: range.to }}
                  label={tr('pharmacy', 'print_statement')}
                  size="default"
                  disabled={!data}
                />
                <CsvExportButton<SupplierStatement, StatementRow>
                  path={`/pharmacy/payables/suppliers/${supplierId}/statement`}
                  params={{ from: range.from, to: range.to }}
                  pickRows={(response) => response.rows}
                  columns={statementCsvColumns()}
                  filename={`supplier-statement-${data?.supplier.code ?? 'supplier'}-${range.from}-to-${range.to}`}
                  size="default"
                  disabled={!data}
                />
              </>
            ) : undefined
          }
        >
          <FilterField label={tr('pharmacy', 'supplier')} className="w-64">
            <Select
              value={supplierId}
              onChange={(event) => {
                onSupplierChange(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{tr('pharmacy', 'select_a_supplier')}</option>
              {suppliers.data?.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplierLabel(supplier)}
                </option>
              ))}
            </Select>
          </FilterField>
          <FilterField label={tr('pharmacy', 'from')}>
            <DatePicker className="w-40" value={draft.from} onChange={(value) => setDraft({ ...draft, from: value })} />
          </FilterField>
          <FilterField label={tr('pharmacy', 'to')}>
            <DatePicker className="w-40" value={draft.to} onChange={(value) => setDraft({ ...draft, to: value })} />
          </FilterField>
          <Button type="submit" variant="secondary" disabled={Boolean(rangeProblem) || !supplierId} loading={statement.isFetching}>
            {tr('pharmacy', 'show')}
          </Button>
        </FilterBar>
      </form>

      {suppliers.error && (
        <Alert variant="destructive" className="mb-4">
          {describeError(suppliers.error)}
        </Alert>
      )}
      {rangeProblem && (
        <Alert variant="warning" className="mb-4">
          {rangeProblem}
        </Alert>
      )}

      {!supplierId && (
        <EmptyState
          title={tr('pharmacy', 'choose_a_supplier')}
          description={tr('pharmacy', 'their_goods_received_returns_and_payments')}
        />
      )}
      {supplierId && statement.isLoading && <LoadingBox />}
      {supplierId && statement.error && (
        <Alert variant="destructive" title={tr('pharmacy', 'the_statement_could_not_be_loaded')}>
          {describeError(statement.error)}
        </Alert>
      )}

      {supplierId && data && (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{data.supplier.name}</span>
            {` · ${data.supplier.code} · terms ${data.supplier.paymentTermsDays} days`}
            {data.supplier.phone ? ` · ${data.supplier.phone}` : ''}
            {data.supplier.taxNumber ? ` · tax no. ${data.supplier.taxNumber}` : ''}
            {` · ${dateOnly(data.from)} to ${dateOnly(data.to)}`}
          </p>

          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label={tr('pharmacy', 'opening_balance')} value={money(data.openingBalanceMinor)} sub={tr('pharmacy', 'owed_at_the_start_of_the_period')} />
            <StatTile label={tr('pharmacy', 'charges')} value={money(data.chargesMinor)} sub={tr('pharmacy', 'goods_received_and_payments_reversed')} />
            <StatTile label={tr('pharmacy', 'credits')} value={money(data.creditsMinor)} sub={tr('pharmacy', 'returns_goods_voided_and_payments')} />
            <StatTile label={tr('pharmacy', 'closing_balance')} value={money(data.closingBalanceMinor)} sub={tr('pharmacy', 'owed_at_the_end_of_the_period')} />
            <StatTile
              label={tr('pharmacy', 'awaiting_approval')}
              value={money(data.pendingApprovalMinor)}
              sub={tr('pharmacy', 'payments_not_yet_in_the_balance')}
              tone={data.pendingApprovalMinor > 0 ? 'warning' : 'default'}
            />
          </div>

          <DataTable
            rows={data.rows}
            rowKey={(row) => `${row.kind}:${row.refId}`}
            empty={tr('pharmacy', 'no_goods_received_returns_or_payments_in_this')}
            footer={
              offset > 0 || offset + data.rows.length < data.totalRows ? (
                <Pager
                  offset={offset}
                  pageSize={PAGE_SIZE}
                  shown={data.rows.length}
                  total={data.totalRows}
                  hasNext={offset + data.rows.length < data.totalRows}
                  busy={statement.isFetching}
                  onChange={setOffset}
                />
              ) : undefined
            }
            columns={[
              { header: tr('pharmacy', 'date'), sort: (row) => row.at, cell: (row) => dateOnly(row.at) },
              {
                header: tr('pharmacy', 'type'),
                sort: (row) => row.kind,
                cell: (row) => <Badge variant={statementKindVariant(row.kind)}>{statementKindLabel(row.kind)}</Badge>,
              },
              { header: tr('pharmacy', 'document'), sort: (row) => row.number, cell: (row) => <span className="font-mono text-xs">{row.number}</span> },
              { header: tr('pharmacy', 'detail'), sort: (row) => row.detail, cell: (row) => row.detail || '—' },
              {
                header: tr('pharmacy', 'due'),
                sort: (row) => (row.kind === 'grn' ? row.dueDate : null),
                cell: (row) => (row.kind === 'grn' && row.dueDate ? dateOnly(row.dueDate) : '—'),
              },
              {
                header: tr('pharmacy', 'amount'),
                align: 'right',
                sort: (row) => signedStatementAmount(row),
                cell: (row) => money(signedStatementAmount(row)),
              },
              { header: tr('pharmacy', 'balance'), align: 'right', sort: (row) => row.balanceMinor, cell: (row) => <strong>{money(row.balanceMinor)}</strong> },
            ]}
          />
        </>
      )}
    </>
  );
}

function PaymentsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [deciding, setDeciding] = useState<{ payment: SupplierPayment; decision: 'approve' | 'reject' } | null>(null);
  const [reversing, setReversing] = useState<SupplierPayment | null>(null);

  const canApprove = can('supplier.payment.approve');
  const canCreate = can('supplier.payment.create');

  const payments = useQuery({
    queryKey: ['payables', 'payments', status, offset],
    queryFn: () =>
      get<SupplierPayment[]>(`/pharmacy/payables/payments${qs({ status, limit: PAGE_SIZE + 1, offset })}`),
    placeholderData: keepPreviousData,
  });

  const rows = payments.data?.slice(0, PAGE_SIZE);
  const hasNext = (payments.data?.length ?? 0) > PAGE_SIZE;

  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField label={tr('pharmacy', 'status')} className="w-56">
            <Select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            >
              <option value="">{tr('pharmacy', 'all_statuses')}</option>
              {SUPPLIER_PAYMENT_STATUS.map((known) => (
                <option key={known} value={known}>
                  {humanize(known)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          status
            ? () => {
                setStatus('');
                setOffset(0);
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={payments.isLoading} refreshing={payments.isFetching}
        error={payments.error}
        rowKey={(row) => row.id}
        empty={
          status
            ? tr('pharmacy', 'no_x_supplier_payments', { value: humanize(status).toLowerCase() })
            : tr('pharmacy', 'no_supplier_payments_yet_use_pay_supplier_to')
        }
        footer={
          offset > 0 || hasNext ? (
            <Pager offset={offset} pageSize={PAGE_SIZE} shown={rows?.length ?? 0} hasNext={hasNext} busy={payments.isFetching} onChange={setOffset} />
          ) : undefined
        }
        columns={[
          {
            header: tr('pharmacy', 'number'),
            sort: (row) => row.number,
            cell: (row) => (
              <div>
                <p className="font-mono text-xs">{row.number}</p>
                <p className="text-xs text-muted-foreground">{dateTime(row.paidAt ?? row.requestedAt)}</p>
              </div>
            ),
          },
          { header: tr('pharmacy', 'supplier'), sort: (row) => row.supplier.name, cell: (row) => row.supplier.name },
          { header: tr('pharmacy', 'mode'), sort: (row) => row.mode, cell: (row) => humanize(row.mode) },
          { header: tr('pharmacy', 'amount'), align: 'right', sort: (row) => row.amountMinor, cell: (row) => money(row.amountMinor) },
          { header: tr('pharmacy', 'reference'), sort: (row) => row.reference, cell: (row) => row.reference || '—' },
          {
            header: tr('pharmacy', 'status'),
            sort: (row) => row.status,
            cell: (row) =>
              row.status === 'reversed' ? (
                <div>
                  <Badge variant="destructive">{tr('pharmacy', 'reversed')}</Badge>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {[row.reversedByName, row.reversedAt ? dateTime(row.reversedAt) : null].filter(Boolean).join(' · ')}
                  </p>
                  {row.reversalReason && <p className="text-xs text-muted-foreground">{row.reversalReason}</p>}
                </div>
              ) : (
                <StatusBadge status={row.status} />
              ),
          },
          { header: tr('pharmacy', 'requested_by'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
          {
            header: tr('pharmacy', 'decided_by'),
            sort: (row) => row.approvedByName,
            cell: (row) => (
              <div>
                <p>{row.approvedByName ?? '—'}</p>
                {row.decisionNote && <p className="text-xs text-muted-foreground">{row.decisionNote}</p>}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                {row.status === 'pending_approval' && (canApprove || canCreate) && (
                  <>
                    {canApprove && (
                      <Button size="sm" variant="outline" onClick={() => setDeciding({ payment: row, decision: 'approve' })}>
                        {tr('pharmacy', 'approve')}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setDeciding({ payment: row, decision: 'reject' })}>
                      {tr('pharmacy', 'reject')}
                    </Button>
                  </>
                )}
                {hasVoucher(row.status) && (
                  <PrintDocumentButton path={`/pharmacy/print/payment-voucher/${row.id}`} label={tr('pharmacy', 'print_payment_voucher')} compact />
                )}
                {canReversePayment(row.status, canApprove) && (
                  <Button size="sm" variant="ghost" onClick={() => setReversing(row)}>
                    {tr('pharmacy', 'reverse')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {deciding && (
        <DecisionDialog payment={deciding.payment} decision={deciding.decision} onClose={() => setDeciding(null)} />
      )}
      {reversing && (
        <ReasonDialog
          title={tr('pharmacy', 'reverse_payment_x', { number: reversing.number })}
          description={tr('pharmacy', 'x_to_x_x', { amountMinor: money(reversing.amountMinor), name: reversing.supplier.name, value: humanize(reversing.mode).toLowerCase() })}
          confirmLabel={tr('pharmacy', 'reverse_payment')}
          keepLabel="Keep payment"
          consequence={
            <Alert variant="warning">
              {tr('pharmacy', 'the_goods_received_this_payment_settled_become')}
            </Alert>
          }
          run={(reason, idempotencyKey) =>
            post(`/pharmacy/payables/payments/${reversing.id}/reverse`, { reason }, { idempotencyKey })
          }
          onDone={() => {
            toast.success(
              tr('pharmacy', 'payment_reversed'),
              `${reversing.number} · ${money(reversing.amountMinor)} · ${reversing.supplier.name}`,
            );
            void queryClient.invalidateQueries({ queryKey: ['payables'] });
            setReversing(null);
          }}
          onClose={() => setReversing(null)}
        />
      )}
    </>
  );
}

function DecisionDialog({
  payment,
  decision,
  onClose,
}: {
  payment: SupplierPayment;
  decision: 'approve' | 'reject';
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: () => {
      const body = { note: note.trim() || undefined };
      return decision === 'approve'
        ? post(`/pharmacy/payables/payments/${payment.id}/approve`, body)
        : post(`/pharmacy/payables/payments/${payment.id}/reject`, body);
    },
    onSuccess: () => {
      toast.success(
        decision === 'approve' ? tr('pharmacy', 'payment_approved') : tr('pharmacy', 'payment_rejected'),
        `${payment.number} · ${money(payment.amountMinor)} · ${payment.supplier.name}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const approving = decision === 'approve';

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${approving ? 'Approve' : 'Reject'} payment ${payment.number}`}
      description={tr('pharmacy', 'x_to_x_x', { amountMinor: money(payment.amountMinor), name: payment.supplier.name, value: humanize(payment.mode).toLowerCase() })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={decide.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            variant={approving ? 'success' : 'destructive'}
            loading={decide.isPending}
            onClick={() => {
              setError(null);
              decide.mutate();
            }}
          >
            {approving ? tr('pharmacy', 'approve_and_pay') : tr('pharmacy', 'reject_payment')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('pharmacy', 'note')} hint={tr('pharmacy', 'optional_kept_with_the_payment')}>
          <Textarea rows={3} maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function ReturnsTab() {
  const [offset, setOffset] = useState(0);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const returns = useQuery({
    queryKey: ['payables', 'returns', offset],
    queryFn: () => get<PurchaseReturnSummary[]>(`/pharmacy/purchase-returns${qs({ limit: PAGE_SIZE + 1, offset })}`),
    placeholderData: keepPreviousData,
  });

  const rows = returns.data?.slice(0, PAGE_SIZE);
  const hasNext = (returns.data?.length ?? 0) > PAGE_SIZE;

  return (
    <>
      <DataTable
        rows={rows}
        loading={returns.isLoading} refreshing={returns.isFetching}
        error={returns.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setViewingId(row.id)}
        empty={tr('pharmacy', 'no_goods_have_been_returned_to_a_supplier_yet')}
        footer={
          offset > 0 || hasNext ? (
            <Pager offset={offset} pageSize={PAGE_SIZE} shown={rows?.length ?? 0} hasNext={hasNext} busy={returns.isFetching} onChange={setOffset} />
          ) : undefined
        }
        columns={[
          {
            header: tr('pharmacy', 'debit_note'),
            sort: (row) => row.number,
            cell: (row) => (
              <div>
                <p className="font-mono text-xs">{row.number}</p>
                <p className="text-xs text-muted-foreground">{dateTime(row.returnedAt)}</p>
              </div>
            ),
          },
          { header: tr('pharmacy', 'supplier'), sort: (row) => row.supplier.name, cell: (row) => row.supplier.name },
          { header: tr('pharmacy', 'store'), sort: (row) => row.store.name, cell: (row) => row.store.name },
          { header: tr('pharmacy', 'against'), sort: (row) => row.grn?.number, cell: (row) => (row.grn ? <span className="font-mono text-xs">{row.grn.number}</span> : '—') },
          { header: tr('pharmacy', 'reason'), sort: (row) => row.reason, cell: (row) => humanize(row.reason) },
          { header: tr('pharmacy', 'credit_note'), sort: (row) => row.creditNoteNo, cell: (row) => row.creditNoteNo || '—' },
          { header: tr('pharmacy', 'lines'), align: 'right', sort: (row) => row.lines, cell: (row) => row.lines },
          {
            header: tr('pharmacy', 'stock_cost'),
            align: 'right',
            sort: (row) => row.stockCostMinor,
            cell: (row) => (row.stockCostMinor === undefined ? '—' : money(row.stockCostMinor)),
          },
          { header: tr('pharmacy', 'credited'), align: 'right', sort: (row) => row.totalMinor, cell: (row) => <strong>{money(row.totalMinor)}</strong> },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <PrintDocumentButton path={`/pharmacy/print/debit-note/${row.id}`} label={tr('pharmacy', 'print_debit_note')} compact />
            ),
          },
        ]}
      />

      {viewingId && <PurchaseReturnDialog id={viewingId} onClose={() => setViewingId(null)} />}
    </>
  );
}

function PurchaseReturnDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['payables', 'return', id],
    queryFn: () => get<PurchaseReturnDetail>(`/pharmacy/purchase-returns/${id}`),
  });
  const data = detail.data;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={data ? tr('pharmacy', 'debit_note_x', { number: data.number }) : tr('pharmacy', 'debit_note')}
      description={data ? `${data.supplier.name} · ${dateTime(data.returnedAt)}` : undefined}
      footer={
        <>
          <PrintDocumentButton path={`/pharmacy/print/debit-note/${id}`} label={tr('pharmacy', 'print_debit_note')} size="default" />
          <Button variant="ghost" onClick={onClose}>
            {tr('pharmacy', 'close')}
          </Button>
        </>
      }
    >
      {detail.isLoading && <LoadingBox />}
      {detail.error && <Alert variant="destructive">{describeError(detail.error)}</Alert>}
      {data && (
        <div className="space-y-4">
          <dl className="grid gap-3 sm:grid-cols-3">
            <Detail label={tr('pharmacy', 'supplier')}>{data.supplier.name}</Detail>
            <Detail label={tr('pharmacy', 'returned_from')}>{data.store.name}</Detail>
            <Detail label={tr('pharmacy', 'reason')}>{humanize(data.reason)}</Detail>
            <Detail label={tr('pharmacy', 'supplier_credit_note')}>{data.creditNoteNo || '—'}</Detail>
            <Detail label={tr('pharmacy', 'against_goods_received')}>
              {data.grn
                ? `${data.grn.number}${data.grn.supplierInvoiceNo ? ` (invoice ${data.grn.supplierInvoiceNo})` : ''}`
                : '—'}
            </Detail>
            <Detail label={tr('pharmacy', 'notes')}>{data.notes || '—'}</Detail>
          </dl>

          <DataTable
            dense
            rows={data.items}
            rowKey={(item) => item.id}
            columns={[
              {
                header: tr('pharmacy', 'item'),
                cell: (item) => (
                  <div>
                    <p className="font-medium">{item.product.genericName}</p>
                    <p className="text-xs text-muted-foreground">{item.product.code}</p>
                  </div>
                ),
              },
              { header: tr('pharmacy', 'batch'), cell: (item) => <span className="font-mono text-xs">{item.batchNo}</span> },
              { header: tr('pharmacy', 'expires'), cell: (item) => dateOnly(item.expiryDate) },
              { header: tr('pharmacy', 'qty_2'), align: 'right', cell: (item) => `${item.qty} ${item.product.unit}` },
              { header: tr('pharmacy', 'unit_cost'), align: 'right', cell: (item) => money(item.unitCostMinor) },
              { header: tr('pharmacy', 'cost'), align: 'right', cell: (item) => money(item.costMinor) },
              {
                header: tr('pharmacy', 'stock_cost'),
                align: 'right',
                cell: (item) => (item.stockCostMinor === undefined ? '—' : money(item.stockCostMinor)),
              },
              { header: tr('pharmacy', 'tax_2'), align: 'right', cell: (item) => money(item.taxMinor) },
              { header: tr('pharmacy', 'total'), align: 'right', cell: (item) => <strong>{money(item.totalMinor)}</strong> },
            ]}
          />

          <dl className="ms-auto grid max-w-xs grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">{tr('pharmacy', 'subtotal')}</dt>
            <dd className="text-end tabular">{money(data.subtotalMinor)}</dd>
            <dt className="text-muted-foreground">{tr('pharmacy', 'tax_2')}</dt>
            <dd className="text-end tabular">{money(data.taxMinor)}</dd>
            <dt className="font-semibold">{tr('pharmacy', 'credited')}</dt>
            <dd className="text-end font-semibold tabular">{money(data.totalMinor)}</dd>
            {data.stockCostMinor !== undefined && (
              <>
                <dt className="text-muted-foreground">{tr('pharmacy', 'stock_left_at_cost')}</dt>
                <dd className="text-end tabular">{money(data.stockCostMinor)}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </Dialog>
  );
}

